import { useDownPayment } from '@finn/ua-featureflags';
import { cn, getFormattedPrice, useIntl } from '@finn/ui-utils';
import get from 'lodash/get';

import { useForBusinessCookie } from '../../helpers';
import { GenericVehicleDetails, VehicleSize } from '../core';
import { PriceInformation } from '../price/PriceInformation';
import { getDefaultTerm, usePreDiscount } from '../product-pricing';
import { ProductImage } from './components/ProductImage';
import { ProductLink } from './components/ProductLink';

type Props = {
  vehicleSize?: VehicleSize;
  vehicle: GenericVehicleDetails;
  cheapestPrice: number;
  versionsCount: number;
  urlParams?: string;
  isForIframe?: boolean;
  partnerDiscount?: number;
  selectedTerm?: number;
  isDownPaymentEnabled?: boolean;
  position?: number;
};

export const ModelCard = ({
  vehicle,
  vehicleSize,
  cheapestPrice,
  versionsCount,
  urlParams,
  isForIframe = false,
  partnerDiscount = 0,
  selectedTerm,
  position,
}: Props) => {
  const i18n = useIntl();
  const isForBusiness = useForBusinessCookie();
  const { isDownPaymentEnabled } = useDownPayment();

  const brandModelLabel = `${vehicle.brand.id} ${vehicle.model}`;
  const perMonthLabel = i18n.formatMessage(
    !isForBusiness ? 'general.perMonth' : 'general.perMonthWithoutVat'
  );

  const priceTotal = cheapestPrice - cheapestPrice * partnerDiscount;
  const priceFormatted = getFormattedPrice(i18n, priceTotal, 0);

  let preDiscountPrice = usePreDiscount({
    vehicle,
    price: cheapestPrice,
    term: selectedTerm,
    isForBusiness,
    isDownPayment: isDownPaymentEnabled,
  });

  const term = selectedTerm || getDefaultTerm(vehicle);

  const priceKey = isDownPaymentEnabled
    ? `downpayment_prices.available_price_list`
    : `price.available_price_list`;

  const price = isForBusiness
    ? get(vehicle, `${priceKey}.b2b_${term}`, 0)
    : get(vehicle, `${priceKey}.b2c_${term}`, 0);

  if (price !== cheapestPrice) {
    preDiscountPrice = undefined;
  }

  if (partnerDiscount > 0) {
    preDiscountPrice = getFormattedPrice(i18n, cheapestPrice, 0);
  }

  return (
    <ProductLink
      vehicle={vehicle}
      urlParams={urlParams}
      isForIframe={isForIframe}
      versionsCount={versionsCount}
      position={position}
    >
      <div className="relative flex rounded bg-snow">
        <ProductImage
          shouldPreload
          shouldShowPlaceholder
          vehicleSize={vehicleSize}
          vehiclePictureURL={vehicle.picture.url}
          altText={brandModelLabel}
        />
      </div>

      <div className="mt-3 grid gap-3">
        <h5
          className={cn(
            'overflow-hidden text-ellipsis py-1 body-14-semibold sm:text-nowrap sm:body-16-semibold'
          )}
        >
          {brandModelLabel}
        </h5>

        {/* Configs amount section */}
        <span className="py-0.5">
          <p className="body-12-light sm:body-14-light">
            {versionsCount}{' '}
            {versionsCount > 1
              ? i18n.formatMessage('plp.cars')
              : i18n.formatMessage('plp.car')}
          </p>
        </span>

        {/* Price section */}
        <div className="body-14-semibold">
          <div className="row-y-2 mb-1 flex flex-wrap items-center md:mb-1.5">
            <span>{i18n.formatMessage('plp.priceFrom')}&nbsp;</span>
            {preDiscountPrice && (
              <div className="mr-1 text-iron line-through">
                {preDiscountPrice}
              </div>
            )}
            <span>{priceFormatted}&nbsp;</span>
            <div className="body-14-light">{perMonthLabel}</div>
            <PriceInformation size="small" />
          </div>
        </div>
      </div>
    </ProductLink>
  );
};
